import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface VolumeTier {
  min_orders: number;
  discount_pct: number;
}

export interface NeuraxPricing {
  per_session_fee: number;
  interview_price: number;
  assessment_price: number;
  token_threshold: number;
  overage_per_1k_tokens: number;
  currency: string;
  volume_tiers: VolumeTier[];
}

async function fetchPricing(companyId: string): Promise<NeuraxPricing | null> {
  const { data, error } = await supabase
    .from('b2b_pricing_configs')
    .select(
      'per_session_fee, interview_price, assessment_price, token_threshold, overage_per_1k_tokens, currency, volume_tiers'
    )
    .eq('b2b_company_id', companyId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw new Error(`b2b_pricing_configs: ${error.message}`);
  return data as NeuraxPricing | null;
}

export function usePricing() {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'pricing', companyId],
    queryFn: () => fetchPricing(companyId),
    staleTime: 5 * 60_000,
  });
}

export function computeActiveTier(tiers: VolumeTier[], orders: number): VolumeTier | null {
  const sorted = [...tiers].sort((a, b) => a.min_orders - b.min_orders);
  let active: VolumeTier | null = null;
  for (const t of sorted) {
    if (orders >= t.min_orders) active = t;
  }
  return active;
}

export function applyDiscount(price: number, tier: VolumeTier | null): number {
  if (!tier) return price;
  return price * (1 - tier.discount_pct / 100);
}
